/**
 * calendarEvents.ts — holidays, events and notes shown on the content calendar.
 *
 * They are all-day DATES in the site's own calendar ("2026-12-25"), not instants, so the site timezone never shifts
 * them. They never block publishing: scheduling on a marked day only shows a heads-up. Sources: typed by hand,
 * a holiday calendar file (.ics, e.g. Google Calendar's "Holidays in …"), or Nager.Date's public-holiday list for the
 * countries it covers (it has no data for several markets and no moving lunar dates, which is why the other two exist).
 */
import { z } from "zod";

export const CALENDAR_EVENT_KINDS = ["holiday", "event", "campaign", "note"] as const;
export const calendarEventKindSchema = z.enum(CALENDAR_EVENT_KINDS);
export type CalendarEventKind = z.infer<typeof calendarEventKindSchema>;

export const CALENDAR_EVENT_KIND_LABELS: Record<CalendarEventKind, string> = {
  holiday: "Holiday",
  event: "Event",
  campaign: "Campaign",
  note: "Note",
};

const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use a date like 2026-12-25.").refine(isRealDate, "That date does not exist.");

function isRealDate(s: string): boolean {
  const [y, m, d] = s.split("-").map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

export const calendarEventSchema = z.object({
  id: z.string(),
  siteId: z.string(),
  title: z.string(),
  kind: calendarEventKindSchema,
  startDate: z.string(),
  /** Last day, inclusive. Equal to startDate for a one-day entry. */
  endDate: z.string(),
  repeatsYearly: z.boolean(),
  source: z.enum(["manual", "ics", "nager"]),
});
export type CalendarEvent = z.infer<typeof calendarEventSchema>;

export const MAX_EVENT_SPAN_DAYS = 60;

export const calendarEventInputSchema = z
  .object({
    title: z.string().trim().min(1, "Give it a name.").max(120),
    kind: calendarEventKindSchema.default("event"),
    startDate: dateOnly,
    endDate: dateOnly.optional(),
    repeatsYearly: z.boolean().default(false),
  })
  .superRefine((v, ctx) => {
    const end = v.endDate ?? v.startDate;
    if (end < v.startDate) ctx.addIssue({ code: "custom", path: ["endDate"], message: "The last day cannot be before the first day." });
    else if (daysBetween(v.startDate, end) >= MAX_EVENT_SPAN_DAYS) ctx.addIssue({ code: "custom", path: ["endDate"], message: `An entry can span at most ${MAX_EVENT_SPAN_DAYS} days.` });
  });
export type CalendarEventInput = z.input<typeof calendarEventInputSchema>;

export const updateCalendarEventSchema = z.object({
  title: z.string().trim().min(1).max(120).optional(),
  kind: calendarEventKindSchema.optional(),
  startDate: dateOnly.optional(),
  endDate: dateOnly.optional(),
  repeatsYearly: z.boolean().optional(),
});

export const importIcsBodySchema = z.object({ siteId: z.string().min(1), ics: z.string().min(1).max(2_000_000) });
export const importHolidaysBodySchema = z.object({
  siteId: z.string().min(1),
  country: z.string().regex(/^[A-Za-z]{2}$/, "Use a two-letter country code, e.g. US."),
  year: z.number().int().min(2000).max(2100),
});
export const importResultSchema = z.object({ added: z.number().int(), skipped: z.number().int(), message: z.string() });
export type ImportResult = z.infer<typeof importResultSchema>;

export const holidayCountrySchema = z.object({ code: z.string(), name: z.string() });
export const holidayCountriesResponseSchema = z.object({ countries: z.array(holidayCountrySchema) });

// --- date helpers (pure string/UTC math: a "date" here has no timezone) ---

export function parseDate(s: string): { y: number; m: number; d: number } {
  const [y, m, d] = s.split("-").map(Number) as [number, number, number];
  return { y, m, d };
}
export function fmtDate(y: number, m: number, d: number): string {
  return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}
export function addDays(date: string, n: number): string {
  const { y, m, d } = parseDate(date);
  const dt = new Date(Date.UTC(y, m - 1, d + n));
  return fmtDate(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
}
export function daysBetween(a: string, b: string): number {
  const pa = parseDate(a);
  const pb = parseDate(b);
  return Math.round((Date.UTC(pb.y, pb.m - 1, pb.d) - Date.UTC(pa.y, pa.m - 1, pa.d)) / 86_400_000);
}

/** One entry on the calendar for a particular year (a yearly entry has one per year). */
export interface EventOccurrence {
  id: string;
  title: string;
  kind: CalendarEventKind;
  startDate: string;
  endDate: string;
  repeatsYearly: boolean;
}

/** The entries that touch [from, to] (inclusive dates), repeating yearly ones into each year the window covers. */
export function expandEvents(events: readonly Pick<CalendarEvent, "id" | "title" | "kind" | "startDate" | "endDate" | "repeatsYearly">[], from: string, to: string): EventOccurrence[] {
  const out: EventOccurrence[] = [];
  const fromYear = parseDate(from).y;
  const toYear = parseDate(to).y;
  for (const e of events) {
    const span = daysBetween(e.startDate, e.endDate);
    const first = parseDate(e.startDate);
    const years = e.repeatsYearly ? range(fromYear - 1, toYear) : [first.y];
    for (const year of years) {
      // 29 Feb in a non-leap year is observed on 28 Feb.
      const start = e.repeatsYearly ? fmtDate(year, first.m, first.m === 2 && first.d === 29 && !isLeap(year) ? 28 : first.d) : e.startDate;
      if (e.repeatsYearly && year < first.y) continue;
      const end = addDays(start, span);
      if (end >= from && start <= to) out.push({ id: e.id, title: e.title, kind: e.kind, startDate: start, endDate: end, repeatsYearly: e.repeatsYearly });
    }
  }
  return out.sort((a, b) => a.startDate.localeCompare(b.startDate) || a.title.localeCompare(b.title));
}
const range = (a: number, b: number) => Array.from({ length: b - a + 1 }, (_, i) => a + i);
const isLeap = (y: number) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;

/** Entries covering one day. */
export function eventsOnDay(occurrences: readonly EventOccurrence[], day: string): EventOccurrence[] {
  return occurrences.filter((o) => o.startDate <= day && day <= o.endDate);
}

/** A short sentence for the heads-up shown when scheduling on a marked day; null when the day is clear. */
export function scheduleHeadsUp(occurrences: readonly EventOccurrence[], day: string): string | null {
  const hit = eventsOnDay(occurrences, day);
  if (hit.length === 0) return null;
  const names = hit.map((h) => `${h.title} (${CALENDAR_EVENT_KIND_LABELS[h.kind].toLowerCase()})`).join(", ");
  return `This day is marked: ${names}.`;
}

// --- .ics import ---

export interface IcsDay {
  title: string;
  startDate: string;
  endDate: string;
  uid: string | null;
}

const unescapeIcs = (s: string) => s.replace(/\\n/gi, " ").replace(/\\([,;\\])/g, "$1").trim();

/**
 * Reads the all-day entries of an iCalendar file. Only whole-day events are taken (DTSTART as a date); timed events
 * are skipped because a holiday list has none and a timed meeting does not belong on this calendar. Repeating rules are
 * ignored (a holiday feed lists each year separately). In .ics, DTEND of an all-day event is the day AFTER the last day.
 */
export interface ParseIcsOptions {
  /** Also take timed entries (webinars, launches) as a day. Needs `toSiteDay` for times given in UTC ("…Z"). Repeating timed entries are skipped. */
  includeTimed?: boolean;
  toSiteDay?: (utcMs: number) => string;
}

export function parseIcsDays(text: string, options: ParseIcsOptions = {}): { days: IcsDay[]; skippedTimed: number } {
  const unfolded = text.replace(/\r\n?/g, "\n").replace(/\n[ \t]/g, "");
  const days: IcsDay[] = [];
  let skippedTimed = 0;
  const blocks = unfolded.split("BEGIN:VEVENT").slice(1);
  for (const raw of blocks) {
    const block = raw.split("END:VEVENT")[0] ?? "";
    const get = (name: string) => {
      const m = new RegExp(`^${name}((?:;[^:\\n]*)?):(.*)$`, "mi").exec(block);
      return m ? { params: m[1] ?? "", value: (m[2] ?? "").trim() } : null;
    };
    const start = get("DTSTART");
    const summary = get("SUMMARY");
    if (!start || !summary) continue;
    const isDate = /VALUE=DATE(?!-)/i.test(start.params) || /^\d{8}$/.test(start.value);
    const toIso = (v: string) => `${v.slice(0, 4)}-${v.slice(4, 6)}-${v.slice(6, 8)}`;
    let startDate: string;
    if (!isDate) {
      const m = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})(Z?)$/.exec(start.value);
      // A repeating meeting would show only its first date, which is wrong; so repeating timed entries are never taken.
      if (!options.includeTimed || !m || /^RRULE/im.test(block)) {
        skippedTimed++;
        continue;
      }
      if (m[7] === "Z") {
        if (!options.toSiteDay) {
          skippedTimed++;
          continue;
        }
        startDate = options.toSiteDay(Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!, +m[4]!, +m[5]!, +m[6]!));
      } else {
        startDate = `${m[1]}-${m[2]}-${m[3]}`;
      }
    } else {
      if (!/^\d{8}$/.test(start.value)) continue;
      startDate = toIso(start.value);
    }
    if (!isRealDate(startDate)) continue;
    const end = get("DTEND");
    let endDate = startDate;
    if (isDate && end && /^\d{8}$/.test(end.value)) {
      const exclusive = toIso(end.value);
      if (isRealDate(exclusive)) endDate = addDays(exclusive, -1);
    }
    if (endDate < startDate) endDate = startDate;
    if (daysBetween(startDate, endDate) >= MAX_EVENT_SPAN_DAYS) continue;
    const title = unescapeIcs(summary.value).slice(0, 120);
    if (!title) continue;
    days.push({ title, startDate, endDate, uid: get("UID")?.value ?? null });
  }
  return { days, skippedTimed };
}

/** Stable key so importing the same file or country twice adds nothing new. */
export function eventSourceKey(source: "ics" | "nager", title: string, startDate: string): string {
  return `${source}:${startDate}:${title.toLowerCase().replace(/\s+/g, " ").trim()}`;
}
